import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { RocketLaunch, ImageSquare, FileXls, TextAlignLeft, ArrowRight } from "@phosphor-icons/react";
import { api, act, useFetch, fmtDate } from "@/lib/api";
import { PageHeader, Panel, Badge, Field, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CleanStart } from "@/components/CleanStart";
import { DeliverabilityPanel } from "@/components/DeliverabilityPanel";

const FilePick = ({ label, icon: Icon, accept, file, onPick, testid, hint }) => (
  <label data-testid={testid} className={`flex items-center gap-3 p-4 border border-dashed rounded-md cursor-pointer transition-colors ${file ? "border-[#A855F7] bg-[#A855F7]/5" : "border-border hover:border-zinc-500"}`}>
    <Icon size={26} className={file ? "text-[#C084FC]" : "text-zinc-500"} />
    <div className="min-w-0 flex-1">
      <div className="text-sm font-medium">{label}</div>
      <div className="text-xs text-zinc-500 truncate">{file ? file.name : hint}</div>
    </div>
    {file && <button type="button" data-testid={`${testid}-clear`} aria-label="Remove file" className="text-zinc-500 hover:text-white text-lg leading-none px-1"
      onClick={(e) => { e.preventDefault(); onPick(null); }}>×</button>}
    <input type="file" accept={accept} className="hidden" onChange={(e) => { onPick(e.target.files[0]); e.target.value = ""; }} />
  </label>
);

const htmlToStars = (html) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const isBold = (n) => {
    const w = n.style?.fontWeight;
    if (w === "normal" || (w && !isNaN(Number(w)) && Number(w) < 600)) return false;
    return n.nodeName === "B" || n.nodeName === "STRONG" || w === "bold" || Number(w) >= 600;
  };
  const walk = (n, inBold) => {
    if (n.nodeType === 3) return n.textContent;
    const bold = !inBold && isBold(n);
    const inner = [...n.childNodes].map((c) => walk(c, inBold || bold)).join("");
    const out = bold && inner.trim() ? inner.replace(/^(\s*)([\s\S]*?)(\s*)$/, "$1**$2**$3") : inner;
    return ["P", "DIV", "LI", "H1", "H2", "H3", "TR"].includes(n.nodeName) ? `${out}\n` : n.nodeName === "BR" ? "\n" : out;
  };
  return walk(doc.body, false).replace(/\n{3,}/g, "\n\n").replace(/\n+$/, "");
};

const PastLists = ({ past, sel, setSel }) => !past?.length ? null : (
  <Field label="Reuse contacts from earlier campaigns" hint="People who opted out, booked a meeting or are mid-sequence are left out automatically.">
    <div className="flex flex-wrap gap-2" data-testid="launch-reuse-list">{past.map((l) => {
      const on = sel.includes(l.id);
      return (
        <button key={l.id} type="button" data-testid={`launch-reuse-${l.id}`} onClick={() => setSel(on ? sel.filter((x) => x !== l.id) : [...sel, l.id])}
          className={`text-xs px-3 py-2 rounded-md border transition-colors ${on ? "border-[#A855F7] bg-[#A855F7]/10 text-white" : "border-border text-zinc-400 hover:border-zinc-500"}`}>
          {on ? "✓ " : ""}{l.name} · {l.contact_count} contacts
        </button>
      );
    })}</div>
  </Field>
);

export const NewLaunch = ({ past, onCreated, preset }) => {
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", text: "", source: "", consent: "published" });
  const [img, setImg] = useState(null);
  useEffect(() => {
    if (!preset) return;
    setF((x) => ({ ...x, name: preset.name, text: preset.text }));
    fetch(preset.image).then((r) => r.blob()).then((b) => setImg(new File([b], preset.image.split("/").pop(), { type: b.type || "image/jpeg" })));
  }, [preset]);
  const [list, setList] = useState(null);
  const [logo, setLogo] = useState(null);
  const [reuse, setReuse] = useState([]);
  const [busy, setBusy] = useState(false);
  const ok = f.name.trim() && f.text.trim().length >= 40 && img && (list || reuse.length) && (!list || f.consent === "later" || f.source.trim());
  const go = async () => {
    const fd = new FormData();
    fd.append("name", f.name); fd.append("offering_text", f.text); fd.append("consent", f.consent); fd.append("consent_source", f.source);
    fd.append("image", img); fd.append("reuse_launch_ids", reuse.join(","));
    if (list) fd.append("contacts", list);
    if (logo) fd.append("logo", logo);
    setBusy(true);
    const r = await act(() => api.post("/launches", fd), "Building your campaign kit");
    setBusy(false);
    if (r) (onCreated ? onCreated(r) : nav(`/launchpad/${r.id}`));
  };
  return (
    <Panel title="New campaign — drop everything here">
      <div className="space-y-4">
        <Field label="Campaign name"><Input data-testid="launch-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Australian Accounting Industry - Pre-Built AI Agents" className="bg-black" /></Field>
        <Field label="About the offering" hint="Paste what it is, who it's for, pricing, the offer and the link. Only facts written here are used. Bold you paste is kept (shown as **like this**); you can also type **stars** yourself.">
          <Textarea data-testid="launch-text-input" rows={9} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })}
            onPaste={(e) => {
              const html = e.clipboardData.getData("text/html");
              if (!html) return;
              e.preventDefault();
              const t = e.target, md = htmlToStars(html);
              const next = f.text.slice(0, t.selectionStart) + md + f.text.slice(t.selectionEnd);
              const caret = t.selectionStart + md.length;
              setF({ ...f, text: next });
              requestAnimationFrame(() => t.setSelectionRange(caret, caret));
            }} className="bg-black" placeholder="We are delighted to introduce..." />
        </Field>
        <div className="grid md:grid-cols-3 gap-3">
          <FilePick testid="launch-image-pick" label="Offering image" icon={ImageSquare} accept="image/png,image/jpeg,image/webp" file={img} onPick={setImg} hint="PNG, JPG or WebP" />
          <FilePick testid="launch-list-pick" label={reuse.length ? "New contact list (optional)" : "Contact list"} icon={FileXls} accept=".csv,.xlsx,.xlsm" file={list} onPick={setList} hint="CSV or XLSX with an Email column" />
          <FilePick testid="launch-logo-pick" label="Company logo (optional)" icon={TextAlignLeft} accept="image/png,image/jpeg,image/webp" file={logo} onPick={setLogo} hint="Used on emails & images" />
        </div>
        <PastLists past={past} sel={reuse} setSel={setReuse} />
        {list && <div className="grid md:grid-cols-2 gap-3">
          <Field label="Why can these contacts be emailed?">
            <div className="flex gap-2">
              {[["published", "Publicly listed business emails"], ["later", "I'll confirm later"]].map(([v, l]) => (
                <Button key={v} type="button" size="sm" variant={f.consent === v ? "default" : "outline"} data-testid={`launch-consent-${v}`} onClick={() => setF({ ...f, consent: v })} className={f.consent === v ? "bg-[#A855F7] hover:bg-[#9333EA] text-white" : "bg-transparent text-zinc-400"}>{f.consent === v ? "✓ " : ""}{l}</Button>
              ))}
            </div>
          </Field>
          {f.consent === "published" && (
            <Field label="Where are they listed?"><Input data-testid="launch-source-input" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} placeholder="https://www.tpb.gov.au/public-register" className="bg-black" /></Field>
          )}
        </div>}
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-zinc-500 max-w-md">MaSa writes the 3-email sequence, newsletter, social posts, a 5-slide carousel and a short video. Nothing is sent until you press Launch.</p>
          <Button data-testid="launch-build-btn" disabled={!ok || busy} onClick={go} className="gap-2 bg-gradient-to-r from-[#6D28D9] via-[#C026D3] to-[#F97316] hover:opacity-90 text-white">
            <RocketLaunch weight="bold" /> {busy ? "Uploading…" : "Build my campaign"}
          </Button>
        </div>
      </div>
    </Panel>
  );
};

export default function Launchpad() {
  const { data, reload } = useFetch("/launches");
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Launchpad" title="One place to launch a campaign." desc="Add the offering text, its image and the contact list. MaSa builds every email, post and video, then waits for your approval." />
      <CleanStart onDone={reload} />
      <DeliverabilityPanel />
      <div className="grid xl:grid-cols-[1fr_360px] gap-6">
        <NewLaunch past={data} />
        <Panel title="Your campaigns">
          {!data?.length ? <Empty>No campaigns built yet</Empty> : (
            <div className="space-y-2" data-testid="launch-list">{data.map((l) => (
              <Link key={l.id} to={`/launchpad/${l.id}`} data-testid={`launch-item-${l.id}`} className="flex items-center gap-3 p-3 border border-border rounded-md hover:border-zinc-500 transition-colors">
                <div className="min-w-0 flex-1"><div className="text-sm font-medium truncate">{l.name}</div><div className="text-[11px] text-zinc-500 font-mono">{fmtDate(l.created_at)} · {l.contact_count ?? l.row_count} contacts</div></div>
                <Badge v={l.status} tone={l.status === "launched" ? "green" : l.status === "failed" ? "red" : "blue"} /><ArrowRight size={14} className="text-zinc-500" />
              </Link>
            ))}</div>
          )}
        </Panel>
      </div>
    </div>
  );
}
